<template>
  <PlaceOrderLayout :summary="summary">
    <WebCard>
      <form data-test="form" autocomplete="off" @submit.prevent="submit">
        <input autocomplete="false" name="hidden" type="text" style="display: none" />
        <div class="flex flex-col md:flex-row">
          <div class="w-full md:w-1/3 sm:pr-5 sidebar-form">
            <div v-if="Object.values(products).length > 1">
              <h3 class="mb-6 text-xl leading-6 font-semibold text-grey-100 poppins">
                {{ $t('place_order.product_fee') }}
              </h3>

              <div class="flex flex-wrap -mx-2 -mb-8">
                <div
                  v-for="product in products"
                  :key="product.code"
                  class="mb-12 px-2"
                  :class="[Object.values(products).length > 3 ? 'w-2/4' : 'w-1/3']"
                >
                  <WebRadioButton v-model="selectedProductCode" :value="product.code">
                    <p class="font-bold">{{ product.fee_name }}</p>
                    <p class="text-sm">
                      {{ product.fee_description }}
                    </p>
                  </WebRadioButton>
                </div>
              </div>
            </div>

            <WebProductInfo
              :product="selectedProduct"
              :summary="summary"
              :isFriendPlan="isFriendPlan"
              :isEsim="isEsim"
              :invitedMonths="invitedMonths"
            />
          </div>

          <div v-if="selectedProductCode == 'alo_0' && isFeeDisabledDatePast" class="w-full md:w-2/3">
            <FlashMessages />
            <div class="w-full mt-0 mb-4">
              <div class="pb-4 sm:w-1/1">
                <WebInfoMessage
                  :isShow="true"
                  :infoText="$t('place_order.info_disabled_at')"
                  :showIcon="false"
                  :messageType="'error'"
                />
              </div>
            </div>
          </div>

          <div v-else class="w-full md:w-2/3">
            <FlashMessages />
            <div v-if="!hasOnlyData" class="w-full mt-0 mb-4">
              <div class="pb-4 sm:w-1/1">
                <WebInfoMessage
                  :isShow="true"
                  :infoText="$t('place_order.info_validation_important')"
                  :showIcon="false"
                  :messageType="'error'"
                />
              </div>
            </div>
            <h3 class="mb-6 text-xl leading-6 font-semibold text-grey-100 poppins">
              {{ $t('place_order.personal_data') }}
            </h3>
            <div class="flex flex-wrap -mx-2 mb-4">
              <div class="w-full sm:w-1/2 px-2 mb-6">
                <WebLabel for="billing_address_first_name">{{ $t('common.fields.first_name') }}</WebLabel>
                <WebInput
                  id="billing_address_first_name"
                  v-model="form.billingAddressFirstName"
                  type="text"
                  name="billing_address_first_name"
                  autocomplete="new-text"
                  :error="errors['billingAddressFirstName']"
                />
                <WebInputError :message="errors['billingAddressFirstName']" />
              </div>

              <div class="w-full sm:w-1/2 px-2 mb-6">
                <WebLabel for="billing_address_last_name">{{ $t('common.fields.last_name') }}</WebLabel>
                <WebInput
                  id="billing_address_last_name"
                  v-model="form.billingAddressLastName"
                  type="text"
                  name="billing_address_last_name"
                  autocomplete="new-text"
                  :error="errors['billingAddressLastName']"
                />
                <WebInputError :message="errors['billingAddressLastName']" />
              </div>

              <div class="w-full sm:w-1/3 px-2 mb-6">
                <WebLabel for="billing_address_document_type">{{ $t('common.fields.document_type') }}</WebLabel>
                <WebSelect
                  id="billing_address_document_type"
                  v-model="form.billingAddressDocumentType"
                  name="billing_address_document_type"
                  autocomplete="new-text"
                  :error="errors['billingAddressDocumentType']"
                >
                  <option value="nif">{{ $t('common.fields.document_type_nif') }}</option>
                  <option value="dni_ext">{{ $t('common.fields.document_type_dni_ext') }}</option>
                  <option value="passport">{{ $t('common.fields.document_type_passport') }}</option>
                </WebSelect>
                <WebInputError :message="errors['billingAddressDocumentType']" />
              </div>

              <div class="w-full sm:w-1/3 px-2 mb-6">
                <WebLabel for="billing_address_document_number">{{ $t('common.fields.document_number') }}</WebLabel>
                <WebInput
                  id="billing_address_document_number"
                  v-model="form.billingAddressDocumentNumber"
                  type="text"
                  name="billing_address_document_number"
                  autocomplete="new-text"
                  :documentType="form.billingAddressDocumentType"
                  :error="errors['billingAddressDocumentNumber']"
                  @setError="(newError) => updateFrontendErrors('billingAddressDocumentNumber', newError)"
                />
                <WebInputError
                  :message="
                    errors['billingAddressDocumentNumber']
                      ? errors['billingAddressDocumentNumber']
                      : frontendErrors['billingAddressDocumentNumber']
                  "
                />
              </div>

              <div class="w-full sm:w-1/3 px-2 mb-6">
                <WebLabel for="birth_date">{{ $t('common.fields.birth_date') }}</WebLabel>
                <WebInput
                  id="birth_date"
                  v-model="form.birthDate"
                  placeholder="dd/mm/yyyy"
                  maxlength="10"
                  type="text"
                  name="birth_date"
                  autocomplete="off"
                  :error="errors['birthDate']"
                  @setError="(newError) => updateFrontendErrors('birthDate', newError)"
                  @input="autoFormatDateInput"
                />
                <WebInputError :message="errors['birthDate'] ? errors['birthDate'] : frontendErrors['birthDate']" />
              </div>

              <div class="w-full sm:w-1/3 px-2 mb-6">
                <div class="flex items-center">
                  <WebLabel for="billing_address_phone">{{ $t('common.fields.phone') }}</WebLabel>
                  <WebTooltip :tooltipText="$t('place_order.tooltip_phone')" />
                </div>
                <WebInputTel
                  id="billing_address_phone"
                  v-model="form.billingAddressPhone"
                  name="billing_address_phone"
                  autocomplete="new-text"
                  :error="errors['billingAddressPhone']"
                  @setError="(newError) => updateFrontendErrors('billingAddressPhone', newError)"
                />
                <WebInputError
                  :message="
                    errors['billingAddressPhone']
                      ? errors['billingAddressPhone']
                      : frontendErrors['billingAddressPhone']
                  "
                />
              </div>

              <div class="w-full sm:w-2/3 px-2 mb-6">
                <WebLabel for="billing_address_email_address">{{ $t('common.fields.email') }}</WebLabel>
                <WebInput
                  id="billing_address_email_address"
                  v-model="form.billingAddressEmail"
                  type="email"
                  name="billing_address_email_address"
                  autocomplete="new-text"
                  :error="errors['billingAddressEmail']"
                  @setError="(newError) => updateFrontendErrors('billingAddressEmail', newError)"
                />
                <WebInputError
                  :message="
                    errors['billingAddressEmail']
                      ? errors['billingAddressEmail']
                      : frontendErrors['billingAddressEmail']
                  "
                />
              </div>

              <h3 class="mt-4 mb-6 text-xl leading-6 font-semibold text-grey-100 poppins">
                {{ $t('place_order.personal_address') }}
              </h3>

              <div class="w-full px-2 mb-6">
                <div class="flex items-center">
                  <WebLabel for="billing_address_address1">{{ $t('common.fields.address1') }}</WebLabel>
                  <WebTooltip v-show="!isEsim" :tooltipText="$t('place_order.tooltip_address')" />
                </div>
                <WebInput
                  id="billing_nothing1"
                  v-model="form.billingAddressAddress1"
                  type="text"
                  name="billing_nothing1"
                  autocomplete="one-time-code"
                  :error="errors['billingAddressAddress1'] || showBillingAddressWarning"
                />
                <WebInputError :message="errors['billingAddressAddress1']" />
                <WebInputError v-if="showBillingAddressWarning" :message="$t('place_order.tooltip_address')" />
              </div>

              <div class="w-full px-2 mb-6">
                <WebLabel for="billing_address_address2" optional>{{ $t('common.fields.address2') }}</WebLabel>
                <WebInput
                  id="billing_address_address2"
                  v-model="form.billingAddressAddress2"
                  type="text"
                  name="billing_address_address2"
                  autocomplete="new-text"
                  :error="errors['billingAddressAddress2']"
                  :warning="form.showAddress2Warning"
                />
                <WebInputError :message="errors['billingAddressAddress2']" />
                <WebInputWarning
                  v-if="form.showAddress2Warning"
                  :message="$t('common.warnings.additional_address_info')"
                />
              </div>
              <input id="billing_address_country" type="hidden" value="es" name="billing_address_country" />
              <!--div class="w-full sm:w-1/2 px-2 mb-6">
                <WebLabel for="billing_address_country">{{ $t('common.fields.country') }}</WebLabel>
                <WebCountrySelect
                  id="billing_address_country"
                  v-model="form.billingAddressCountry"
                  :locale="$i18n.locale"
                  name="billing_address_country"
                  autocomplete="new-text"
                  disabled
                  :error="errors['billingAddressCountry']"
                />
                <WebInputError :message="errors['billingAddressCountry']" />
              </div-->

              <div class="w-full sm:w-1/2 px-2 mb-6">
                <WebLabel for="billing_address_postal_code">{{ $t('common.fields.postal_code') }}</WebLabel>
                <WebInput
                  id="billing_address_postal_code"
                  v-model="form.billingAddressPostalCode"
                  type="text"
                  name="billing_address_postal_code"
                  autocomplete="new-text"
                  :error="errors['billingAddressPostalCode']"
                  @change="postalCodeChanged($event, 'billingAddressProvince')"
                />
                <WebInputError :message="errors['billingAddressPostalCode']" />
              </div>

              <div class="w-full sm:w-1/2 px-2 mb-6">
                <WebLabel for="billing_address_city">{{ $t('common.fields.city') }}</WebLabel>
                <WebInput
                  id="billing_address_city"
                  v-model="form.billingAddressCity"
                  type="text"
                  name="billing_address_city"
                  autocomplete="new-text"
                  :error="errors['billingAddressCity']"
                />
                <WebInputError :message="errors['billingAddressCity']" />
              </div>

              <input
                id="billing_address_province"
                v-model="form.billingAddressProvince"
                type="hidden"
                name="billing_address_province"
              />
              <!--div class="w-full sm:w-1/2 px-2 mb-6">
                <WebLabel for="billing_address_province">{{ $t('common.fields.province') }}</WebLabel>
                <WebProvinceSelect
                  id="billing_address_province"
                  v-model="form.billingAddressProvince"
                  name="billing_address_province"
                  autocomplete="new-text"
                  :error="errors['billingAddressProvince']"
                />
                <WebInputError :message="errors['billingAddressProvince']" />
              </div-->
            </div>

            <div v-show="!isEsim" :class="{ 'm-b-8': form.useBillingAddressForShippingAddress }">
              <h3 class="mb-6 text-xl leading-6 font-semibold text-grey-100 poppins">
                {{ $t('place_order.shipping_address') }}
              </h3>

              <div class="flex items-start">
                <WebCheckbox
                  id="shipping_should_be_made_to_a_same_address"
                  v-model="form.useBillingAddressForShippingAddress"
                  name="shipping_should_be_made_to_a_same_address"
                  :error="errors['useBillingAddressForShippingAddress']"
                >
                  {{ $t('place_order.shipping_should_be_made_to_a_same_address') }}
                </WebCheckbox>
              </div>

              <div v-show="!form.useBillingAddressForShippingAddress" class="flex flex-wrap -mx-2 mt-6 mb-4">
                <div class="w-full sm:w-1/2 px-2 mb-6">
                  <WebLabel for="shipping_address_first_name">{{ $t('common.fields.first_name') }}</WebLabel>
                  <WebInput
                    id="shipping_address_first_name"
                    v-model="form.shippingAddressFirstName"
                    type="text"
                    name="shipping_address_first_name"
                    autocomplete="new-text"
                    :error="errors['shippingAddressFirstName']"
                  />
                  <WebInputError :message="errors['shippingAddressFirstName']" />
                </div>

                <div class="w-full sm:w-1/2 px-2 mb-6">
                  <WebLabel for="shipping_address_last_name">{{ $t('common.fields.last_name') }}</WebLabel>
                  <WebInput
                    id="shipping_address_last_name"
                    v-model="form.shippingAddressLastName"
                    type="text"
                    name="shipping_address_last_name"
                    autocomplete="new-text"
                    :error="errors['shippingAddressLastName']"
                  />
                  <WebInputError :message="errors['shippingAddressLastName']" />
                </div>

                <div class="w-full px-2 mb-6">
                  <div class="flex items-center">
                    <WebLabel for="shipping_address_nothing1">{{ $t('common.fields.address1') }}</WebLabel>
                    <WebTooltip v-show="!isEsim" :tooltipText="$t('place_order.tooltip_address')" />
                  </div>
                  <WebInput
                    id="shipping_nothing1"
                    v-model="form.shippingAddressAddress1"
                    type="text"
                    name="shipping_nothing1"
                    autocomplete="one-time-code"
                    :error="errors['shippingAddressAddress1'] || showShippingAddressWarning"
                  />
                  <WebInputError :message="errors['shippingAddressAddress1']" />
                  <WebInputError v-if="showShippingAddressWarning" :message="$t('place_order.tooltip_address')" />
                </div>

                <div class="w-full px-2 mb-6">
                  <WebLabel for="shipping_address_address2" optional>{{ $t('common.fields.address2') }}</WebLabel>
                  <WebInput
                    id="shipping_address_address2"
                    v-model="form.shippingAddressAddress2"
                    type="text"
                    name="shipping_address_address2"
                    autocomplete="new-text"
                    :error="errors['shippingAddressAddress2']"
                    :warning="form.showAddress2WarningShipping"
                  />
                  <WebInputError :message="errors['shippingAddressAddress2']" />
                  <WebInputWarning
                    v-if="form.showAddress2WarningShipping"
                    :message="$t('common.warnings.additional_address_info')"
                  />
                </div>

                <!-- <div class="w-full sm:w-1/2 px-2 mb-6">
                  <WebLabel for="shipping_address_country">{{ $t('common.fields.country') }}</WebLabel>
                  <WebCountrySelect
                    id="shipping_address_country"
                    v-model="form.shippingAddressCountry"
                    :locale="$i18n.locale"
                    name="shipping_address_country"
                    autocomplete="new-text"
                    :error="errors['shippingAddressCountry']"
                  />
                  <WebInputError :message="errors['shippingAddressCountry']" />
                </div> -->

                <div class="w-full sm:w-1/2 px-2 mb-6">
                  <WebLabel for="shipping_address_postal_code">{{ $t('common.fields.postal_code') }}</WebLabel>
                  <WebInput
                    id="shipping_address_postal_code"
                    v-model="form.shippingAddressPostalCode"
                    type="text"
                    name="shipping_address_postal_code"
                    autocomplete="new-text"
                    :error="errors['shippingAddressPostalCode']"
                    @change="postalCodeChanged($event, 'shippingAddressProvince')"
                  />
                  <WebInputError :message="errors['shippingAddressPostalCode']" />
                </div>

                <div class="w-full sm:w-1/2 px-2 mb-6">
                  <WebLabel for="shipping_address_city">{{ $t('common.fields.city') }}</WebLabel>
                  <WebInput
                    id="shipping_address_city"
                    v-model="form.shippingAddressCity"
                    type="text"
                    name="shipping_address_city"
                    autocomplete="new-text"
                    :error="errors['shippingAddressCity']"
                  />
                  <WebInputError :message="errors['shippingAddressCity']" />
                </div>

                <input
                  id="shipping_address_phone"
                  v-model="form.billingAddressPhone"
                  type="hidden"
                  name="shipping_address_phone"
                />
                <!-- <div class="w-full sm:w-1/2 px-2 mb-6">
                  <div class="flex items-center">
                    <WebLabel for="shipping_address_phone">{{ $t('common.fields.phone') }}</WebLabel>
                    <WebTooltip :tooltipText="$t('place_order.tooltip_phone_shipping')" />
                  </div>
                  <WebInputTel
                    id="shipping_address_phone"
                    v-model="form.billingAddressPhone"
                    name="shipping_address_phone"
                    autocomplete="new-text"
                    :error="errors['shippingAddressPhone']"
                  />
                  <WebInputError :message="errors['shippingAddressPhone']" />
                </div> -->

                <input
                  id="shipping_address_province"
                  v-model="form.shippingAddressProvince"
                  type="hidden"
                  name="shipping_address_province"
                />
                <!-- <div class="w-full sm:w-1/2 px-2 mb-6">
                  <WebLabel for="shipping_address_province" optional>{{ $t('common.fields.province') }}</WebLabel>
                  <WebProvinceSelect
                    v-else
                    id="shipping_address_province"
                    v-model="form.shippingAddressProvince"
                    name="shipping_address_province"
                    autocomplete="new-text"
                    :error="errors['shippingAddressProvince']"
                  />
                  <WebInputError :message="errors['shippingAddressProvince']" />
                </div> -->
              </div>
            </div>

            <div v-show="isPickup" :class="{ 'm-b-8': !form.useAloIccid }">
              <h3 class="mb-6 text-xl leading-6 font-semibold text-grey-100 poppins">
                {{ $t('place_order.use_alo_iccid') }}
              </h3>

              <div class="flex items-start">
                <WebCheckbox
                  id="use_alo_iccid"
                  v-model="form.useAloIccid"
                  name="use_alo_iccid"
                  :error="errors['useAloIccid']"
                >
                  {{ $t('place_order.use_alo_iccid_info') }}
                </WebCheckbox>
              </div>

              <div v-show="form.useAloIccid" class="flex flex-wrap -mx-2 mt-4 mb-4">
                <div class="w-full sm:w-1/2 px-2 mb-6">
                  <div class="flex items-center">
                    <WebLabel for="alo_iccid">{{ $t('place_order.alo_iccid') }}</WebLabel>
                    <WebTooltip :tooltipText="$t('place_order.alo_iccid_tooltip')" :isAloIccid="true" />
                  </div>
                  <WebInput
                    id="alo_iccid"
                    v-model="form.aloIccid"
                    type="text"
                    name="alo_iccid"
                    autocomplete="new-text"
                    :error="errors['aloIccid']"
                  />
                  <WebInputError :message="errors['aloIccid']" />
                </div>
              </div>
            </div>

            <div :class="{ hidden: hasOnlyData, 'm-b-8': true }">
              <h3 class="mt-2 mb-6 text-xl leading-6 font-semibold text-grey-100 poppins">
                {{ $t('place_order.lines') }}
              </h3>
              <p class="mb-4">{{ $t('place_order.intro_lines') }}</p>
              <div class="flex flex-wrap w-full">
                <div class="w-full">
                  <WebPortabilityRadioButton v-model="form.isPortability" />

                  <WebInputError :message="errors['isPortability']" />
                </div>

                <div v-show="form.isPortability" class="w-full sm:w-1/2 px-2 mt-4">
                  <WebLabel for="phone_portability">{{ $t('common.fields.phone_portability') }}</WebLabel>
                  <WebInputTel
                    id="phone_portability"
                    v-model="form.phonePortability"
                    name="phone_portability"
                    autocomplete="new-text"
                    :isDisabledFlags="true"
                    :error="errors['phonePortability']"
                    @setError="(newError) => updateFrontendErrors('phonePortability', newError)"
                  />
                  <WebInputError
                    :message="
                      errors['phonePortability'] ? errors['phonePortability'] : frontendErrors['phonePortability']
                    "
                  />
                  <WebCheckbox
                    id="portabilityConditions"
                    v-model="form.portabilityConditions"
                    class="mt-4"
                    name="portabilityConditions"
                  >
                    <!-- eslint-disable vue/no-v-html -->
                    <span
                      v-html="
                        $t('common.legal.accept_portability_conditions', {
                          urlGeneral: 'https://doc.alo.es/condiciones_generales.pdf',
                        })
                      "
                    ></span>
                    <!-- eslint-enable vue/no-v-html -->
                  </WebCheckbox>
                  <WebInputError :message="errors['portabilityConditions']" />
                </div>
                <div class="w-full px-2 mt-4">
                  <div class="sm:w-1/2">
                    <WebInfoMessage
                      :isShow="form.isPortability === true"
                      :infoText="$t('place_order.info_portability_important')"
                      :showIcon="false"
                      :messageType="'error'"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="mb-6 border-t border-gray-200" />

            <WebCheckbox id="generalConditions" v-model="form.generalConditions" name="generalConditions">
              <!-- eslint-disable vue/no-v-html -->
              <span
                v-html="
                  $t('common.legal.accept', {
                    urlGeneral: 'https://doc.alo.es/condiciones_generales.pdf',
                    urlPrivacy: 'https://doc.alo.es/privacidad.pdf',
                  })
                "
              ></span>
              <!-- eslint-enable vue/no-v-html -->
            </WebCheckbox>
            <WebInputError :message="errors['generalConditions']" />

            <div class="mt-8">
              <WebButton :loading="form.processing">
                <span v-if="selectedProduct.fee_price == 0">{{ $t('place_order.order_continue') }}</span>
                <span v-else>{{ $t('place_order.credit_card_continue') }}</span>
              </WebButton>
            </div>
            <WebPaymentImages v-if="selectedProduct.fee_price != 0" />
          </div>
        </div>
      </form>
    </WebCard>
  </PlaceOrderLayout>
</template>

<script>
import { DateTime } from 'luxon'
import { useForm } from '@inertiajs/vue2'
import PlaceOrderLayout from '@/Web/Layouts/PlaceOrder.vue'
import WebCard from '@/Web/Components/WebCard.vue'
import WebLabel from '@/Web/Components/WebLabel.vue'
import WebInput from '@/Web/Components/WebInput.vue'
import WebInputError from '@/Web/Components/WebInputError.vue'
import WebInputWarning from '@/Web/Components/WebInputWarning.vue'
import WebCheckbox from '@/Web/Components/WebCheckbox.vue'
import WebSelect from '@/Web/Components/WebSelect.vue'
import WebProvinceSelect from '@/Web/Components/WebProvinceSelect.vue'
import WebButton from '@/Web/Components/WebButton.vue'
import WebInputTel from '@/Web/Components/WebInputTel.vue'
import FlashMessages from '@/Web/Shared/FlashMessages.vue'
import WebCountrySelect from '@/Web/Components/WebCountrySelect.vue'
import WebRadioButton from '@/Web/Components/WebRadioButton.vue'
import WebPortabilityRadioButton from '@/Web/Components/WebPortabilityRadioButton.vue'
import WebProductInfo from '@/Web/Components/WebProductInfo.vue'
import WebInfoMessage from '@/Web/Components/WebInfoMessage.vue'
import WebTooltip from '@/Web/Components/WebTooltip.vue'
import WebPaymentImages from '../Components/WebPaymentImages.vue'
import { Loader } from 'google-maps'

export default {
  components: {
    WebRadioButton,
    WebPortabilityRadioButton,
    //WebCountrySelect,
    WebInputTel,
    WebButton,
    // WebProvinceSelect,
    WebSelect,
    WebCheckbox,
    WebInputError,
    WebInputWarning,
    WebInput,
    WebLabel,
    WebCard,
    PlaceOrderLayout,
    FlashMessages,
    WebProductInfo,
    WebInfoMessage,
    WebTooltip,
    WebPaymentImages,
  },

  props: {
    products: {
      type: Object,
      required: true,
    },
    isFriendPlan: {
      type: Boolean,
      required: true,
    },
    isEsim: {
      type: Boolean,
      required: true,
    },
    isPickup: {
      type: Boolean,
      required: true,
    },
    hasOnlyData: {
      type: Boolean,
      required: true,
    },
    errors: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      selectedProductCode: Object.keys(this.products)[0],
      frontendErrors: {
        phonePortability: '',
        billingAddressPhone: '',
        birthDate: '',
        billingAddressEmail: '',
        billingAddressDocumentNumber: '',
      },
      hasFriendPlan: this.isFriendPlan ?? false,
      form: useForm({
        isCompany: false,
        birthDate: '',

        isPortability: false,
        phonePortability: '',

        useBillingAddressForShippingAddress: true,

        useAloIccid: false,

        aloIccid: '',

        invitedMonths: 0,

        billingAddressCompanyName: '',
        billingAddressCompanyNumber: '',
        billingAddressDocumentType: 'nif',
        billingAddressDocumentNumber: '',
        billingAddressTreatment: '',
        billingAddressFirstName: '',
        billingAddressLastName: '',
        billingAddressPhone: '',
        billingAddressEmail: '',
        billingAddressAddress1: '',
        billingAddressAddress2: '',
        billingAddressPostalCode: '',
        billingAddressCity: '',
        billingAddressProvince: '',
        billingAddressCountry: 'es',

        shippingAddressCompanyName: '',
        shippingAddressFirstName: '',
        shippingAddressLastName: '',
        shippingAddressPhone: '',
        shippingAddressAddress1: '',
        shippingAddressAddress2: '',
        shippingAddressPostalCode: '',
        shippingAddressCity: '',
        shippingAddressProvince: '',
        shippingAddressCountry: 'es',

        generalConditions: false,
        dataProtection: false,
        safeLine: false,
        portabilityConditions: false,

        showAddress2Warning: false,
        showAddress2WarningShipping: false,
        warningIsShow: false,
        warningShippingIsShow: false,

        showBillingAddressWarning: false,
        showShippingAddressWarning: false,
      }),
    }
  },

  computed: {
    selectedProduct() {
      return this.products[this.selectedProductCode]
    },
    invitedMonths() {
      return this.hasFriendPlan ? this.selectedProduct.invited_months : 0
    },
    isFeeDisabledDatePast() {
      if (!this.selectedProduct || !this.selectedProduct.fee_disabled_at) return false

      const feeDisabledDate = DateTime.fromISO(this.selectedProduct.fee_disabled_at)
      const today = DateTime.now()

      return feeDisabledDate < today
    },
    summary() {
      return {
        fees: [
          {
            code: this.selectedProduct.fee_code,
            name: this.selectedProduct.fee_name,
            price: this.selectedProduct.fee_price,
            duration: this.selectedProduct.fee_duration ? this.selectedProduct.fee_duration : '12',
            duration_days: this.selectedProduct.fee_duration_days ? this.selectedProduct.fee_duration_days : null,
            monthlyPrice: this.selectedProduct.fee_monthly_price,
            quantity: 1,
          },
        ],
        shippings: [
          {
            price: this.selectedProduct.shipping_price,
            quantity: 1,
          },
        ],
        deposits: [
          {
            name: this.selectedProduct.deposit_name ?? null,
            price: this.selectedProduct.deposit_price ?? 0,
            quantity: 1,
          },
        ],
      }
    },
    negativeUseBillingAddressForShippingAddress: {
      get() {
        return !this.form.useBillingAddressForShippingAddress
      },

      set(value) {
        this.form.useBillingAddressForShippingAddress = !value
      },
    },
  },

  watch: {
    // Save form data
    form: {
      deep: true,
      handler(form) {
        if (window.localStorage) {
          // if isPortability is false, delete portabilityPhone and portabilityConditions
          if (false === form.data().isPortability) {
            delete form.data().phonePortability
            delete form.data().portabilityConditions
          }

          window.localStorage.setItem('place-order-form', JSON.stringify({ ...form.data(), date: new Date() }))
        }
      },
    },
    'form.billingAddressAddress2': function (newValue) {
      if ('' !== newValue) {
        this.form.showAddress2Warning = false
      }
    },
    'form.shippingAddressAddress2': function (newValue) {
      if ('' !== newValue) {
        this.form.showAddress2WarningShipping = false
      }
    },
    'form.billingAddressAddress1': function (newValue) {
      if ('' !== newValue && '' === this.form.billingAddressAddress2 && !this.form.warningIsShow) {
        this.form.warningIsShow = true
        this.form.showAddress2Warning = true
      }
      this.showBillingAddressWarning = this.checkInvalidAddress(newValue)
    },
    'form.shippingAddressAddress1': function (newValue) {
      if ('' !== newValue && '' === this.form.shippingAddressAddress2 && !this.form.warningShippingIsShow) {
        this.form.warningShippingIsShow = true
        this.form.showAddress2WarningShipping = true
      }
      this.showShippingAddressWarning = this.checkInvalidAddress(newValue)
    },
  },

  mounted() {
    this.loadGoogleMaps()
    // Retrieve saved form
    if (window.localStorage) {
      const placeOrderFormData = JSON.parse(window.localStorage.getItem('place-order-form'))

      // Delete portabilityPhone and portabilityConditions from local storage if isPortability is false
      if (placeOrderFormData && false === placeOrderFormData.isPortability) {
        delete placeOrderFormData.phonePortability
        delete placeOrderFormData.portabilityConditions
      }

      if (placeOrderFormData && 15 >= (new Date(placeOrderFormData.date) - new Date()) / (1000 * 60 * 60 * 24)) {
        this.form = {
          ...this.form,
          ...placeOrderFormData,
        }
      }
    }

    // Send track events
    if (window.gtag && window.googleAdsId) {
      window.gtag('event', 'conversion', { send_to: `${window.googleAdsId}/pk3rCIm6xtQCEJ2m-qAC` })
    }

    if (window.fbq) {
      window.fbq('track', 'Lead')
    }

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'begin_checkout',
      ecommerce: {
        items: [
          {
            item_name: 'aló Móvil',
            item_id: this.selectedProductCode,
            price: this.selectedProduct.fee_price / 1.21,
            item_brand: 'aló',
            item_category: 'Telecommunications',
            item_category2: 'Mobile plan',
            quantity: 1,
          },
        ],
      },
    })

    if (tracify) {
      tracify.captureAddToCart()
    }
  },
  methods: {
    postalCodeChanged(e, field) {
      const codes = {
        '04': 'Almería',
        11: 'Cádiz',
        14: 'Córdoba',
        18: 'Granada',
        21: 'Huelva',
        23: 'Jaén',
        29: 'Málaga',
        41: 'Sevilla',
        22: 'Huesca',
        44: 'Teruel',
        50: 'Zaragoza',
        33: 'Asturias',
        '07': 'Balears, Illes',
        35: 'Palmas, Las',
        38: 'Santa Cruz de Tenerife',
        39: 'Cantabria',
        '05': 'Ávila',
        '09': 'Burgos',
        24: 'León',
        34: 'Palencia',
        37: 'Salamanca',
        40: 'Segovia',
        42: 'Soria',
        47: 'Valladolid',
        49: 'Zamora',
        '02': 'Albacete',
        13: 'Ciudad Real',
        16: 'Cuenca',
        19: 'Guadalajara',
        45: 'Toledo',
        '08': 'Barcelona',
        17: 'Girona',
        25: 'Lleida',
        43: 'Tarragona',
        '03': 'Alicante/Alacant',
        12: 'Castellón/Castelló',
        46: 'Valencia/València',
        '06': 'Badajoz',
        10: 'Cáceres',
        15: 'Coruña, A',
        27: 'Lugo',
        32: 'Ourense',
        36: 'Pontevedra',
        28: 'Madrid',
        30: 'Murcia',
        31: 'Navarra',
        '01': 'Araba/Álava',
        48: 'Bizkaia',
        20: 'Gipuzkoa',
        26: 'Rioja, La',
        51: 'Ceuta',
        52: 'Melilla',
      }
      //console.log(e, codes[e.substring(0, 2)])
      this.form[field] = codes[e.substring(0, 2)]
    },
    updateFrontendErrors(field, newError) {
      this.frontendErrors[field] = newError
    },
    async loadGoogleMaps() {
      const options = {
        region: 'ES',
        language: 'es',
        libraries: ['places'],
      }

      const loader = new Loader('AIzaSyAtA6ZxCzsFYAFnQL3AKm3oHJku7YFO_dg', options)
      const google = await loader.load()

      const autoCompleteOptions = {
        componentRestrictions: { country: 'es' },
        fields: ['address_components'],
        types: ['address'],
      }

      const setupAutocomplete = (inputId, addressProperty) => {
        const input = document.getElementById(inputId)
        const autocomplete = new google.maps.places.Autocomplete(input, autoCompleteOptions)

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace()
          const address = place.address_components

          setTimeout(() => {
            //console.log(address)
            this.form[addressProperty + 'AddressAddress1'] =
              address.find((component) => component.types.includes('route'))?.long_name +
              ', ' +
              (address.find((component) => component.types.includes('street_number'))?.long_name || 'S/N')
          }, 100)

          this.form[addressProperty + 'AddressPostalCode'] = address.find((component) =>
            component.types.includes('postal_code')
          )?.long_name

          this.form[addressProperty + 'AddressCity'] = address.find((component) =>
            component.types.includes('locality')
          )?.long_name

          let province = address.find((component) => component.types.includes('administrative_area_level_2'))
            ? address.find((component) => component.types.includes('administrative_area_level_2'))
            : address.find((component) => component.types.includes('administrative_area_level_1'))
          this.form[addressProperty + 'AddressProvince'] = province.long_name
        })
      }

      setupAutocomplete('billing_nothing1', 'billing')
      setupAutocomplete('shipping_nothing1', 'shipping')
    },
    submit() {
      const { resellerCode, subResellerCode } = this.getResellerCodes()
      const referralCode = this.hasFriendPlan ? this.getReferralCode() : null
      const birthDate = this.form.birthDate

      // Separate day, month and year
      const parts = birthDate.split('/')
      const day = parts[0]
      const month = parts[1]
      const year = parts[2]

      // Format date as Y-m-d
      const formattedBirthDate = `${year}-${month}-${day}`

      this.form
        .transform((data) => ({
          ...data,
          birthDate: formattedBirthDate,
          billingAddressCompanyName: data.isCompany ? data.billingAddressCompanyName : null,
          billingAddressCompanyNumber: data.isCompany ? data.billingAddressCompanyNumber : null,
          billingAddressDocumentType: !data.isCompany ? data.billingAddressDocumentType : null,
          billingAddressDocumentNumber: !data.isCompany ? data.billingAddressDocumentNumber : null,
          billingAddressTreatment: !data.isCompany ? data.billingAddressTreatment : null,
          shippingAddressPhone: data.billingAddressPhone,
          phonePortability: data.isPortability ? data.phonePortability : null,
          portabilityConditions: data.isPortability ? data.portabilityConditions : false,
          useAloIccid: data.useAloIccid ? true : false,
          aloIccid: data.useAloIccid ? data.aloIccid : null,
          invitedMonths: this.hasFriendPlan ? this.selectedProduct.invited_months : 0,
          resellerCode: resellerCode,
          subResellerCode: subResellerCode,
          referralCode: referralCode,
          isEsim: this.isEsim ? true : false,
        }))
        .post(`/${this.$i18n.locale}/place-order/${this.selectedProductCode}`)
    },

    getReferralCode() {
      const referralCookie = this.getCookie('al_f')

      let referralCode = null

      if (referralCookie) {
        return referralCookie
      }

      return referralCode
    },

    getResellerCodes() {
      const resellerCookie = this.getCookie('hs_r')

      let resellerCode = null
      let subResellerCode = null

      if (resellerCookie) {
        const resellerCookieParts = resellerCookie.split('-')
        resellerCode = parseInt(resellerCookieParts[0])
        subResellerCode = resellerCookieParts.slice(1).join('_')
      }

      return {
        resellerCode,
        subResellerCode,
      }
    },

    getCookie(cookieName) {
      let decodedCookie = decodeURIComponent(document.cookie)
      let parts = decodedCookie.split(';')
      for (let i = 0; i < parts.length; i++) {
        let cookie = parts[i].trim()
        if (0 === cookie.indexOf(cookieName)) {
          return cookie.substring(cookieName.length + 1, cookie.length)
        }
      }
      return null
    },

    autoFormatDateInput() {
      let value = this.form.birthDate.replace(/\D/g, '')

      if (2 < value.length) {
        value = value.replace(/(\d{2})(\d)/, '$1/$2')
      }

      if (5 < value.length) {
        value = value.replace(/(\d{2})(\d)/, '$1/$2')
      }

      if (6 < value.length) {
        value = value.replace(/(\d{4})/, '$1')
      }

      this.form.birthDate = value
    },

    checkInvalidAddress(value) {
      const invalidKeywords = ['apartado', 'apdo', 'correos', 'buzón', 'mrw']
      return invalidKeywords.some((keyword) => value?.toLowerCase().includes(keyword))
    },
  },
}
</script>

<style>
.m-b-8 {
  margin-bottom: 30px;
}
.custom-row {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-top: 20px;
}
.custom-col-40 {
  flex: 0 0 30%;
}
.custom-col-60 {
  flex: 0 0 70%;
}
.custom-col-60 .info-portability {
  width: 100%;
}
.custom-col-60 .info-portability p.text-sm:first-child {
  font-weight: bold !important;
}
</style>
